import axios from 'axios';
import { 
  Tag, 
  TagWithCreator, 
  CreateTagData, 
  UpdateTagData, 
  TagFilterOptions,
  AssignTagData,
  TagCategory,
  TagStatistics,
  TagCategoryStats
} from '../types/tag';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });
          
          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

class TagService {
  // Tag Management
  static async getAllTags(filters: TagFilterOptions = {}): Promise<TagWithCreator[]> {
    const params = new URLSearchParams();
    
    if (filters.category) params.append('category', filters.category);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters.created_by) params.append('created_by', filters.created_by.toString());
    if (filters.search) params.append('search', filters.search);

    const response = await apiClient.get(`/tags?${params.toString()}`);
    return response.data;
  }

  static async getTagById(id: number): Promise<TagWithCreator> {
    const response = await apiClient.get(`/tags/${id}`);
    return response.data;
  }

  static async createTag(data: CreateTagData): Promise<Tag> {
    const response = await apiClient.post('/tags', data);
    return response.data;
  }

  static async updateTag(id: number, data: UpdateTagData): Promise<Tag> {
    const response = await apiClient.put(`/tags/${id}`, data);
    return response.data;
  }

  static async deleteTag(id: number): Promise<void> {
    await apiClient.delete(`/tags/${id}`);
  }

  static async getTagCategories(): Promise<TagCategory[]> {
    const response = await apiClient.get('/tags/categories');
    return response.data;
  }

  static async getTagStatistics(): Promise<TagStatistics[]> {
    const response = await apiClient.get('/tags/statistics');
    return response.data;
  }

  static async getTagCategoryStats(): Promise<TagCategoryStats[]> {
    const response = await apiClient.get('/tags/category-stats');
    return response.data;
  }

  static async getTagUsageCount(id: number): Promise<{ tag_id: number; usage_count: number }> {
    const response = await apiClient.get(`/tags/${id}/usage-count`);
    return response.data;
  }

  // Schedule Tag Management
  static async getScheduleTags(scheduleId: number): Promise<any[]> {
    const response = await apiClient.get(`/tags/schedules/${scheduleId}/tags`);
    return response.data;
  }

  static async getBulkScheduleTags(scheduleIds: number[]): Promise<Record<number, any[]>> {
    const response = await apiClient.post('/tags/schedules/bulk-tags', {
      schedule_ids: scheduleIds
    });
    return response.data;
  }

  static async assignTagToSchedule(scheduleId: number, tagId: number): Promise<any> {
    const response = await apiClient.post(`/tags/schedules/${scheduleId}/tags`, {
      tag_id: tagId
    });
    return response.data;
  }

  static async removeTagFromSchedule(scheduleId: number, tagId: number): Promise<void> {
    await apiClient.delete(`/tags/schedules/${scheduleId}/tags/${tagId}`);
  }

  static async bulkUpdateScheduleTags(scheduleId: number, tagIds: number[]): Promise<any[]> {
    const response = await apiClient.put(`/tags/schedules/${scheduleId}/tags`, {
      tag_ids: tagIds
    });
    return response.data;
  }

  static async getSchedulesByTag(tagId: number, startDate?: Date, endDate?: Date): Promise<any[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate.toISOString());
    if (endDate) params.append('end_date', endDate.toISOString());

    const response = await apiClient.get(`/tags/${tagId}/schedules?${params.toString()}`);
    return response.data;
  }
}

export const tagService = TagService; 